export const STRIKE_COUNT_OPTIONS = [
  { value: 5, label: 'ATM+5' },
  { value: 10, label: 'ATM+10' },
  { value: 15, label: 'ATM+15' },
  { value: 20, label: 'ATM+20' },
  { value: 30, label: 'ATM+30' },
  { value: -1, label: 'ALL' },
];

export const STRIKE_SELECTION_OPTIONS = [
  { value: 'near-atm', label: 'Near ATM' },
  { value: 'only-atm', label: 'Only ATM' },
  { value: 'near-atm-ce', label: 'Near ATM CE' },
  { value: 'near-atm-pe', label: 'Near ATM PE' },
  { value: 'only-ce', label: 'Only CE' },
  { value: 'only-pe', label: 'Only PE' },
  { value: 'all', label: 'All Strikes' },
];

export const RESOLUTION_MAP = {
  '1m': 1, '3m': 3, '5m': 5, '15m': 15, '30m': 30, '1h': 60,
};

export const MULTI_STRIKE_PERIODS = ['1D', '2D', '3D', '4D', '5D'];

export const PERIOD_DAYS = {
  '1D': 1, '2D': 2, '3D': 3, '4D': 4, '5D': 5,
};

export const formatOI = (val) => {
  if (!val || isNaN(val)) return '-';
  const absVal = Math.abs(val);
  if (absVal >= 10000000) return `${(val / 10000000).toFixed(2)}cr`;
  if (absVal >= 100000) return `${(val / 100000).toFixed(2)}L`;
  return val.toLocaleString('en-IN');
};

/* Series colors — curated palette for up to 30+ series in light/dark modes */
const COLORS = [
  '#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed',
  '#db2777', '#0891b2', '#65a30d', '#ea580c', '#4f46e5',
  '#be123c', '#0d9488', '#ca8a04', '#9333ea', '#e11d48',
  '#0284c7', '#16a34a', '#c2410c', '#7e22ce', '#0369a1',
  '#15803d', '#a16207', '#6d28d9', '#be185d', '#0e7490',
  '#4d7c0f', '#9a3412', '#6b21a8', '#9f1239', '#0c4a6e',
];

export const getSeriesColor = (index) => {
  return COLORS[index % COLORS.length];
};

/**
 * Build strike → { tokenCE, tokenPE } mapping from option chain.
 */
export const buildStrikeTokenMap = (optionChain) => {
  const map = {};
  optionChain?.forEach((item) => {
    const strike = item.strikePrice;
    const entry = map[strike] || {};
    if (item.instrumentType === 'CE') entry.tokenCE = item.instrumentToken;
    if (item.instrumentType === 'PE') entry.tokenPE = item.instrumentToken;
    map[strike] = entry;
  });
  return map;
};

/**
 * Get sorted unique strike array from option chain.
 */
export const getStrikeList = (optionChain, atmIndex, strikeCount) => {
  if (strikeCount === -1) {
    // All strikes
    const set = new Set();
    optionChain.forEach((item) => set.add(item.strikePrice));
    return [...set].sort((a, b) => a - b);
  }

  const start = Math.max(atmIndex - 1 - strikeCount * 2, 0);
  const end = Math.min(atmIndex + strikeCount * 2, optionChain.length);
  const set = new Set();
  for (let i = start; i <= end; i++) {
    if (optionChain[i]?.strikePrice) set.add(optionChain[i].strikePrice);
  }
  return [...set].sort((a, b) => a - b);
};

/**
 * Apply a quick selection preset to strikes.
 */
export const applyStrikeSelection = (allStrikes, atmStrike, selectionValue) => {
  switch (selectionValue) {
    case 'near-atm': {
      const atmIdx = allStrikes.indexOf(atmStrike);
      if (atmIdx === -1) return allStrikes.map((s) => ({ strike: s, enableCE: true, enablePE: true }));
      const start = Math.max(0, atmIdx - 5);
      const end = Math.min(allStrikes.length, atmIdx + 6);
      return allStrikes.slice(start, end).map((s) => ({ strike: s, enableCE: true, enablePE: true }));
    }
    case 'only-atm':
      return atmStrike ? [{ strike: atmStrike, enableCE: true, enablePE: true }] : [];
    case 'near-atm-ce': {
      const atmIdx = allStrikes.indexOf(atmStrike);
      if (atmIdx === -1) return [];
      const start = Math.max(0, atmIdx - 5);
      const end = Math.min(allStrikes.length, atmIdx + 6);
      return allStrikes.slice(start, end).map((s) => ({ strike: s, enableCE: true, enablePE: false }));
    }
    case 'near-atm-pe': {
      const atmIdx = allStrikes.indexOf(atmStrike);
      if (atmIdx === -1) return [];
      const start = Math.max(0, atmIdx - 5);
      const end = Math.min(allStrikes.length, atmIdx + 6);
      return allStrikes.slice(start, end).map((s) => ({ strike: s, enableCE: false, enablePE: true }));
    }
    case 'only-ce':
      return allStrikes.map((s) => ({ strike: s, enableCE: true, enablePE: false }));
    case 'only-pe':
      return allStrikes.map((s) => ({ strike: s, enableCE: false, enablePE: true }));
    case 'all':
    default:
      return allStrikes.map((s) => ({ strike: s, enableCE: true, enablePE: true }));
  }
};

/**
 * Get the data field name for a given mode
 */
export const getDataField = (mode) => {
  switch (mode) {
    case 'oi': return 'oi';
    case 'iv': return 'ivc';
    case 'lp':
    default: return 'c';
  }
};

/**
 * Get the display label for a mode
 */
export const getModeLabel = (mode) => {
  switch (mode) {
    case 'lp': return 'Multi-Strike Price';
    case 'oi': return 'Multi-Strike OI';
    case 'pcr': return 'Multi-Strike PCR';
    case 'iv': return 'Multi-Strike IV';
    default: return 'Multi-Strike';
  }
};

export const calculateChartDates = (period, holidays = [], specialSessions = []) => {
  const maximumDays = PERIOD_DAYS[period] || 1;
  let count = 0;
  let d = new Date();
  
  const p = (n) => String(n).padStart(2, '0');
  const fmt = (date) => `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;

  const toStr = `${fmt(d)}T${p(d.getHours())}:${p(d.getMinutes())}`;
  
  while (count < maximumDays) {
    const dateStr = fmt(d);
    const day = d.getDay();
    const isWeekend = day === 6 || day === 0;
    const isHoliday = holidays.some(h => h.formattedDate === dateStr);
    const isSpecial = specialSessions.some(s => s.formattedDate === dateStr);

    if ((!isWeekend && !isHoliday) || isSpecial) {
      count++;
    }
    
    if (count === maximumDays) {
      break;
    }
    d.setDate(d.getDate() - 1);
  }
  
  const fromStr = `${fmt(d)}T09:15`;
  return { fromStr, toStr };
};
